/** Multi-sort builder (Item A) in the dock — a sibling of CrmFilterPanel. Lists the active sort
 * levels in precedence order (level 1 = primary) with a per-level direction toggle, reorder (▲ ▼),
 * and remove (×), plus an "Add sort" picker for any not-yet-sorted column. Emits an ordered
 * SortLevel[] that CrmListGrid applies to the grid's column state (which persists in the saved view
 * for free). The grid stays the source of truth — this panel is a controlled view over it. */
import styles from './crm.module.css';
import type { CrmGridColumn } from '../../types';
import { addSort, moveSort, removeSort, setDir, type SortDir, type SortLevel } from './crmSortModel';

interface Props {
  columns: CrmGridColumn[];
  model: SortLevel[];
  onChange: (model: SortLevel[]) => void;
}

export function CrmSortPanel({ columns, model, onChange }: Props) {
  const labelByKey = new Map(columns.map((c) => [c.field_key, c.label]));
  const label = (colId: string) => labelByKey.get(colId) ?? colId;
  // only columns not already in the model are offered to add (a column sorts on one key at a time)
  const addable = columns.filter((c) => !model.some((s) => s.colId === c.field_key));

  return (
    <div className={styles.sortPanel}>
      {model.length === 0 && (
        <div className={styles.gridEmpty}>Not sorted. Add a column below to sort by it.</div>
      )}

      {model.map((level, i) => (
        <div key={level.colId} className={styles.sortRow}>
          <span className={styles.sortRank} title={`Sort priority ${i + 1}`}>{i + 1}</span>
          <span className={styles.sortField} title={label(level.colId)}>{label(level.colId)}</span>
          <select
            className={styles.sortDir}
            value={level.sort}
            aria-label={`${label(level.colId)} sort direction`}
            onChange={(e) => onChange(setDir(model, level.colId, e.target.value as SortDir))}
          >
            <option value="asc">A → Z</option>
            <option value="desc">Z → A</option>
          </select>
          <span className={styles.sortMove}>
            <button
              className={styles.gridMiniBtn}
              aria-label={`Move ${label(level.colId)} up`}
              disabled={i === 0}
              onClick={() => onChange(moveSort(model, level.colId, 'up'))}
            >▲</button>
            <button
              className={styles.gridMiniBtn}
              aria-label={`Move ${label(level.colId)} down`}
              disabled={i === model.length - 1}
              onClick={() => onChange(moveSort(model, level.colId, 'down'))}
            >▼</button>
          </span>
          <button
            className={styles.filterRemove}
            aria-label={`Remove ${label(level.colId)} sort`}
            onClick={() => onChange(removeSort(model, level.colId))}
          >×</button>
        </div>
      ))}

      <div className={styles.filterActions}>
        <select
          className={styles.sortAdd}
          value=""
          aria-label="Add a sort column"
          disabled={addable.length === 0}
          onChange={(e) => { if (e.target.value) onChange(addSort(model, e.target.value, 'asc')); }}
        >
          <option value="">{addable.length ? '+ Add sort…' : 'All columns sorted'}</option>
          {addable.map((c) => <option key={c.field_key} value={c.field_key}>{c.label}</option>)}
        </select>
        {model.length > 0 && (
          <button className={styles.filterClear} onClick={() => onChange([])}>Clear all</button>
        )}
      </div>
    </div>
  );
}
